import React from "react";
import { ShieldCheck, Leaf, MapPin, Wrench } from "lucide-react";

const points = [
  {
    icon: ShieldCheck,
    title: "Licensed & insured",
    desc: "EWRB registered electricians and certified installers, fully insured for your peace of mind.",
  },
  {
    icon: Leaf,
    title: "Built for Taranaki",
    desc: "Designed for our coastal weather — from salt air to strong westerlies, your system is built to last.",
  },
  {
    icon: MapPin,
    title: "New Plymouth based",
    desc: "Locally owned and operated. We cover New Plymouth city and the wider Taranaki region.",
  },
  {
    icon: Wrench,
    title: "10 yr workmanship",
    desc: "Backed by manufacturer warranties and our own 10-year workmanship guarantee.",
  },
];

const steps = [
  { n: "01", t: "Free site review", d: "We assess your roof, usage and goals on a quick call or visit." },
  { n: "02", t: "Tailored design", d: "A custom system spec with competitive pricing." },
  { n: "03", t: "Paperwork handled", d: "Grid application, council and warranties — we sort it all." },
  { n: "04", t: "Install & switch on", d: "Typical installation time is within 1 week. Welcome to free sunshine." },
];

const About = () => {
  return (
    <>
      <section id="about" className="relative py-24 lg:py-32 bg-[#E8EAE6]/60">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.3em] text-[#5C6B61]">
              · About Central Solar
            </div>
            <h2
              data-testid="about-heading"
              className="font-display text-4xl sm:text-5xl tracking-tight font-medium mt-4 text-[#1A3626] leading-[1.05]"
            >
              Kiwi-owned. Solar-obsessed. Quietly excellent.
            </h2>
            <p className="mt-6 text-base text-[#5C6B61] leading-relaxed">
              We started Central Solar because we wanted to do solar properly —
              honest advice, premium gear, beautiful installs and the kind of
              after-care you&rsquo;d give your own whānau.
            </p>

            <div className="mt-10 relative rounded-3xl overflow-hidden border border-[#D2D7D0]">
              <img
                src="/kaitake-ranges.png"
                alt="Kaitake Ranges, New Plymouth"
                className="w-full h-72 object-cover"
              />
            </div>
          </div>

          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
            {points.map((p) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.title}
                  data-testid={`about-point-${p.title.replace(/\s+/g, "-").toLowerCase()}`}
                  className="rounded-3xl bg-white border border-[#D2D7D0] p-7 hover:-translate-y-1 transition-transform duration-500"
                  style={{ boxShadow: "0 8px 32px rgba(26,54,38,0.05)" }}
                >
                  <div className="h-11 w-11 rounded-2xl bg-[#1A3626] text-[#F5C242] flex items-center justify-center">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-display text-xl mt-6 text-[#1A3626]">{p.title}</h3>
                  <p className="text-sm text-[#5C6B61] mt-3 leading-relaxed">{p.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section id="process" className="relative py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.3em] text-[#5C6B61]">· How it works</div>
            <h2
              data-testid="process-heading"
              className="font-display text-4xl sm:text-5xl tracking-tight font-medium mt-4 text-[#1A3626]"
            >
              Four steps from sunlight to switch-on.
            </h2>
          </div>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {steps.map((s, i) => (
              <div
                key={s.n}
                data-testid={`process-step-${s.n}`}
                className="relative rounded-3xl border border-[#D2D7D0] bg-white p-7 hover:border-[#1A3626]/40 transition-colors"
                style={{ boxShadow: "0 8px 32px rgba(26,54,38,0.04)" }}
              >
                <div className="flex items-baseline justify-between">
                  <span className="font-display text-3xl text-[#F5C242]">{s.n}</span>
                  <span className="text-[10px] uppercase tracking-[0.25em] text-[#5C6B61]">
                    Step {i + 1}
                  </span>
                </div>
                <h3 className="font-display text-xl mt-6 text-[#1A3626]">{s.t}</h3>
                <p className="text-sm text-[#5C6B61] mt-3 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default About;
